import React, { useEffect, useRef, useState } from 'react';
import { TheStorySectionProps } from '../../types';
import { typography, colors } from '../../styles/designTokens';
import machineImageAsset from '../../assets/machineGood.webp';
import AnnotationPoint from '../AdvantageCards/AnnotationPoint';

const TheStorySection: React.FC<TheStorySectionProps> = ({ machineImage }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  
  // Refs for typewriter - עדכון ישיר של DOM בלי React re-render
  const textRefs = useRef<Record<string, HTMLSpanElement | null>>({
    vision: null,
    greeting: null,
    title: null,
    paragraph1: null,
    paragraph2: null,
    paragraph3: null,
    paragraph4: null,
  });
  
  // טקסטים לאפקט typewriter
  const texts = {
    vision: 'החזון שלנו הוא להפוך כל תספורת לחוויה נקיה וחדשנית ללא לכלוך מיותר.',
    greeting: 'נעים להכיר',
    title: 'AiRY',
    paragraph1: 'מכונה המשלבת מערכת שאיבה חכמה, יחד עם סכין ייחודית בגימור עדין.',
    paragraph2: 'אין יותר לכלוך בכל הסביבה, אין יותר שערות דבוקות ותחושת עקצוץ.',
    paragraph3: 'הסכין הייחודית מעניקה תספורת רכה, מדויקת ונעימה במיוחד,\nכך שהתספורת הופכת לחוויה נוחה ונקיה מאי פעם.',
    paragraph4: 'זה הזמן לגלות איך פעולת התספורת,\nיכולה להיות הרבה יותר מפנקת – לכם ולסביבה שלכם.'
  };


  // בדיקה אם החלק גלוי - עם IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2, // כשכ-20% מהחלק גלוי
        rootMargin: '0px',
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const emptyTexts = {
      vision: '',
      greeting: '',
      title: '',
      paragraph1: '',
      paragraph2: '',
      paragraph3: '',
      paragraph4: '',
    };

    let cancelled = false;
    const timeouts: number[] = [];
    const intervals: number[] = [];

    const sleep = (ms: number) => new Promise<void>(resolve => {
      const timeoutId = window.setTimeout(() => resolve(), ms);
      timeouts.push(timeoutId);
    });

    const typeText = (text: string, key: string, speed: number) =>
      new Promise<void>(resolve => {
        let index = 0;
        const intervalId = window.setInterval(() => {
          if (cancelled) return;
          index += 1;
          // עדכון ישיר של DOM - בלי React!
          if (textRefs.current[key]) {
            textRefs.current[key]!.textContent = text.slice(0, index);
          }
          if (index >= text.length) {
            window.clearInterval(intervalId);
            resolve();
          }
        }, speed);
        intervals.push(intervalId);
      });

    const run = async () => {
      const steps: Array<{ key: keyof typeof textRefs.current; text: string; delay: number; speed: number }> = [
        { key: 'vision', text: texts.vision, delay: 50, speed: 25 },
        { key: 'greeting', text: texts.greeting, delay: 60, speed: 25 },
        { key: 'title', text: texts.title, delay: 40, speed: 25 },
        { key: 'paragraph1', text: texts.paragraph1, delay: 70, speed: 25 },
        { key: 'paragraph2', text: texts.paragraph2, delay: 50, speed: 25 },
        { key: 'paragraph3', text: texts.paragraph3, delay: 60, speed: 25 },
        { key: 'paragraph4', text: texts.paragraph4, delay: 65, speed: 25 },
      ];

      for (const step of steps) {
        if (cancelled) return;
        await sleep(step.delay);
        if (cancelled) return;
        await typeText(step.text, step.key, step.speed);
      }
    };

    run();

    return () => {
      cancelled = true;
      timeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
      intervals.forEach(intervalId => window.clearInterval(intervalId));
    };
  }, [isVisible, texts.greeting, texts.paragraph1, texts.paragraph2, texts.paragraph3, texts.paragraph4, texts.title, texts.vision]);

  return (
    <section 
      ref={sectionRef}
      id="story" 
      className="relative overflow-hidden" 
      style={{ 
        // רקע אפור כהה/פחם - כמו בתמונה (dark grey/charcoal)
        backgroundColor: '#2a2a2a',
        overflowX: 'hidden', // מונע overflow אופקי
        width: '100%',
        maxWidth: '100%',
        // תאורה אלכסונית מהפינה השמאלית העליונה - צללים רכים ואלכסוניים
        backgroundImage: `
          /* תאורה אלכסונית מהפינה השמאלית העליונה - יוצרת צללים אלכסוניים */
          linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 20%, transparent 50%),
          /* גרדיאנט אנכי לעומק */
          linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, transparent 30%, rgba(0, 0, 0, 0.2) 100%),
          /* זוהר צהוב-חם בפינה הימנית התחתונה */
          radial-gradient(ellipse 800px 400px at 85% 95%, rgba(255, 220, 150, 0.2) 0%, rgba(255, 200, 100, 0.1) 30%, transparent 60%),
          /* צללים אלכסוניים נוספים לתחושת עומק */
          linear-gradient(135deg, transparent 0%, rgba(0, 0, 0, 0.15) 50%, transparent 100%)
        `,
        backgroundSize: '100% 100%, 100% 100%, 100% 100%, 100% 100%',
        backgroundPosition: 'top left, center, bottom right, center',
        backgroundRepeat: 'no-repeat',
        paddingTop: 'clamp(40px, 6vw, 80px)', // ריווח מוגדל למעלה
        paddingBottom: 'clamp(50px, 6vw, 100px)', // ריווח מוגדל למטה - מובייל: 50px, דסקטופ: 100px
        // אפקט משטח רפלקטיבי בתחתית - זוהר רך
        boxShadow: 'inset 0 -150px 300px -100px rgba(255, 220, 150, 0.12)',
        position: 'relative',
      }}
    >
      <div className="container mx-auto" style={{ maxWidth: '95%', paddingLeft: 'clamp(8px, 2vw, 16px)', paddingRight: 'clamp(8px, 2vw, 16px)' }}>
        {/* <div className="grid md:grid-cols-[1fr_0.75fr] items-start gap-2"> */}
         <div className="grid md:grid-cols-[1.2fr_0.6fr] items-start gap-4">
          {/* Right Side - Text Content - Responsive - רוחב מורחב */}
          <div className="text-right order-1 md:order-1 flex flex-col gap-3" style={{ maxWidth: '100%', minWidth: 0, alignItems: 'flex-end', width: '100%' }}>
            {/* Text Container - תיבה ריקה שמחזיקה את כל הטקסטים */}
            <div style={{ width: '100%', willChange: 'content', contain: 'layout', backfaceVisibility: 'hidden' }}>
            {/* Vision Text - מעל "נעים להכיר" */}
            <p
              style={{
                fontSize: 'clamp(1.125rem, 2.2vw, 1.7rem)', // מובייל: 18px, דסקטופ: 27px
                fontWeight: 400,
                lineHeight: 1.6,
                textAlign: 'right',
                direction: 'rtl',
                marginBottom: 'clamp(6px, 1vw, 10px)',
                width: '100%',
                color: colors.gold.base,
                minHeight: '1.6em',
              }}
            >
              <span 
                ref={(el) => { textRefs.current.vision = el; }}
                style={{
                  display: 'inline',
                }}
              >
              </span>
            </p>
            {/* Headlines - עם typewriter effect - מיושר ימינה כמו כל הטקסט */}
            <div className="flex flex-col" style={{ lineHeight: 1, marginBottom: '0.5rem', width: '100%', alignItems: 'flex-end' }}>
              <p 
                className="font-normal greeting-paragraph"
                style={{ 
                  fontSize: 'clamp(1.25rem, 3vw, 2.25rem)', // מובייל: 20px, דסקטופ: 36px
                  letterSpacing: typography.body.letterSpacing,
                  direction: 'rtl',
                  textAlign: 'right',
                  lineHeight: 1,
                  marginBottom: 0,
                  marginTop: 0,
                  padding: 0,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  color: '#FFFFFF',
                }}
              >
              <span 
                ref={(el) => { textRefs.current.greeting = el; }}
                style={{
                  display: 'inline',
                }}
              >
              </span>
                <span 
                  style={{ 
                    fontFamily: typography.heading.fontFamily,
                    fontWeight: 900,
                    direction: 'ltr',
                    color: '#FFFFFF',
                    marginRight: '2px',
                    fontSize: '0.9em',
                    transform: 'translate(-6px, 5px)',
                  }}
                >
                  <span 
                    ref={(el) => { textRefs.current.title = el; }}
                    style={{
                      display: 'inline',
                    }}
                  >
                  </span>
                </span>
              </p>
            </div>
              
            {/* Body Text - מדויק כמו בקנווה - כל הטקסט סטטי - רוחב מורחב */}
            <div className="text-lg flex flex-col story-text-container" style={{ maxWidth: '100%', width: '100%', lineHeight: 1.6, gap: 0, willChange: 'content', contain: 'layout', backfaceVisibility: 'hidden' }}>
              {/* כל הפסקאות - סטטיות - צמודות ללא רווחים */}
              <p 
                style={{ 
                  fontSize: 'clamp(1.125rem, 2.2vw, 1.7rem)',
                  letterSpacing: typography.body.letterSpacing,
                  direction: 'rtl',
                  textAlign: 'right',
                  lineHeight: 1.6,
                  margin: 0,
                  padding: 0,
                  color: '#FFFFFF',
                  minHeight: '3em',
                  fontWeight: 700,
                }}
              >
                <span 
                  ref={(el) => { textRefs.current.paragraph1 = el; }}
                  style={{ display: 'inline' }}
                >
                </span>
              </p>
              <p 
                style={{ 
                  fontSize: 'clamp(1.125rem, 2.2vw, 1.7rem)',
                  letterSpacing: typography.body.letterSpacing,
                  direction: 'rtl',
                  textAlign: 'right',
                  lineHeight: 1.6,
                  margin: 0,
                  padding: 0,
                  color: colors.gold.base,
                  minHeight: '1.6em',
                }}
              >
                <span 
                  ref={(el) => { textRefs.current.paragraph2 = el; }}
                  style={{ display: 'inline' }}
                >
                </span>
              </p>
              <p 
                style={{ 
                  fontSize: 'clamp(1.125rem, 2.2vw, 1.7rem)',
                  letterSpacing: typography.body.letterSpacing,
                  direction: 'rtl',
                  textAlign: 'right',
                  lineHeight: 1.6,
                  margin: 0,
                  padding: 0,
                  whiteSpace: 'pre-line',
                  color: '#FFFFFF',
                  minHeight: '3.2em',  // 3 שורות במקום 2
                 }}
              >
                <span 
                  ref={(el) => { textRefs.current.paragraph3 = el; }}
                  style={{ display: 'inline' }}
                >
                </span>
              </p>
              <p 
                style={{ 
                  fontSize: 'clamp(1.125rem, 2.2vw, 1.7rem)',
                  letterSpacing: typography.body.letterSpacing,
                  direction: 'rtl',
                  textAlign: 'right',
                  lineHeight: 1.6,
                  margin: 0,
                  marginTop: 'clamp(20px, 3vw, 30px)',
                  padding: 0,
                  whiteSpace: 'pre-line',
                  color: '#FFFFFF',
                  minHeight: '3.2em',  // 3 שורות במקום 2
                }}
              >
                <span 
                  ref={(el) => { textRefs.current.paragraph4 = el; }}
                  style={{ display: 'inline' }}
                >
                </span>
              </p>
            </div>
            </div>
            
            {/* Call-to-Action Button - ממורכז באמצע הטקסט */}
            <div style={{ marginTop: 'clamp(15px, 2vw, 30px)', width: '100%', position: 'relative', direction: 'rtl', textAlign: 'right', paddingRight: 'clamp(8px, 3vw, 40px)' }}>
              <a
                href="/payment"
                className="group order-button-switch order-button-bounce rounded-full"
                style={{ 
                  background: colors.accentSpecial.buttonGradientDiagonal,
                  padding: 'clamp(9px, 1.5vw, 14px) clamp(16px, 3vw, 28px) clamp(9px, 1.5vw, 14px) clamp(52px, 6.5vw, 70px)',
                  fontSize: 'clamp(13px, 2vw, 20px)',
                  fontWeight: '700',
                  color: '#000000',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  opacity: isVisible ? 1 : 0,
                  transition: 'opacity 1s ease-out 2s',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  position: 'relative',
                  animation: isVisible ? 'orderButtonColorSwitch 3s steps(1, end) infinite 2s' : 'none',
                  letterSpacing: typography.body.letterSpacing,
                  maxWidth: '100%',
                }}
              >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>הזמינו עכשיו ותיהנו מחוויית תספורת</span>
                
                {/* עיגול שחור עם חץ כתום - position absolute */}
                <div 
                  className="flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-75 ease-out group-hover:-translate-x-2"
                  style={{
                    position: 'absolute',
                    left: '0px',
                    width: 'clamp(36px, 5.5vw, 58px)',
                    height: 'clamp(36px, 5.5vw, 58px)',
                    backgroundColor: '#000000',
                    border: '2px solid #D4943A',
                  }}
                >
                  {/* אייקון חץ */}
                  <svg 
                    className="transition-transform duration-75 ease-out rotate-45 group-hover:!rotate-0"
                    style={{
                      width: 'clamp(18px, 4vw, 26px)',
                      height: 'clamp(18px, 4vw, 26px)',
                      color: colors.gold.base,
                    }}
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M19 12H5M5 12L12 5M5 12L12 19" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </a>
            </div>
          </div>
            
          {/* Left Side - Machine Image - Responsive - מקביל למלל מלמעלה */}
          {/* <div className="relative order-2 md:order-2 flex items-center justify-center" style={{ minHeight: 'auto', alignSelf: 'center', marginTop: '0' }}> */}
            <div className="relative order-2 md:order-2 flex items-start justify-center" style={{ minHeight: 'auto', alignSelf: 'flex-start', marginTop: '0', position: 'sticky', top: '20px' }}>
            <div 
              ref={imageWrapperRef}
              className="machine-ellipse-wrapper relative z-10 flex items-center justify-center w-full"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: 'translateY(0)',
                filter: isVisible ? 'blur(0px)' : 'blur(10px)',
                transition: 'opacity 1.1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s, filter 1.1s ease-out 0.4s',
              }}
            >
              {/* תמונת המכונה - גודל מוקטן - מוזזת שמאלה */}
              <div 
                className="machine-image-wrapper relative"
                style={{
                  width: 'clamp(150px, 15vw, 200px)', // מובייל: 150px, דסקטופ: 200px
                  maxWidth: '220px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  marginLeft: 'clamp(-40px, -6vw, -25px)', // הזזה שמאלה
                }}
              >
                {/* Machine Image - נקייה לחלוטין ללא אפקטים */}
                <img 
                  src={machineImageAsset} 
                  alt="מכונת התספורת AiRY" 
                  className="machine-image"
                  width="1513"
                  height="3502"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    position: 'relative',
                    zIndex: 2,
                  }}
                  loading="lazy"
                />

                {/* Annotations - כרטיסי הסבר ממוקמים נכון סביב המכונה */}
                {[
                  {
                    id: 1,
                    x: 50, // מרכז הלהב בראש המכונה
                    y: 8, // ראש המכונה - הלהב
                    cardX: 120, // כרטיס מימין למכונה - הורחק עוד יותר מהמכונה
                    cardY: 12, // כרטיס הורד קצת למטה
                    title: 'להב מקצועי',
                    description: 'בייצור מיוחד\nלשימוש יעיל ונח',
                    lineDirection: 'right' as const,
                  },
                  {
                    id: 2,
                    x: 50, // מרכז המכונה - איזור המיכל איחסון (במיקום AIRY)
                    y: 45, // מתחת למלל של AIRY - הורד בערך 3 ס"מ
                    cardX: -22, // כרטיס משמאל למכונה - הורחק מהמכונה
                    cardY: 48, // כרטיס הורד למטה
                    title: 'מיכל שאיבה',
                    description: 'לאיסוף השיער הקצוץ.\nלמניעת לכלוך וגרד', // שורה נפרדת
                    lineDirection: 'left' as const,
                  },
                  {
                    id: 3,
                    x: 50, // מרכז המכונה - החלק של המנוע
                    y: 65, // הועלה ב-6 ס"מ למעלה - מקום החורים (מעל הכפתור) - היה 78%, עכשיו 65%
                    cardX: 125, // כרטיס מימין למכונה - הורחק עוד יותר מהמכונה
                    cardY: 67, // הועלה ב-6.5 ס"מ למעלה - מקביל לנקודה - היה 80%, עכשיו 67%
                    title: '2 מנועים עוצמתיים', // בשורה אחת
                    description: 'לביצוע 2 פעולות בו זמנית',
                    lineDirection: 'right' as const,
                  },
                ].map((annotation, index) => (
                  <AnnotationPoint
                    key={annotation.id}
                    annotation={annotation}
                    index={index}
                    isVisible={isVisible}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animation for pulse and glow effects */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.8;
          }
        }
        
        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.6;
            transform: scale(1);
          }
          50% {
            opacity: 0.9;
            transform: scale(1.05);
          }
        }
        
        @keyframes rotateLight {
          0% {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          100% {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }
        
        @keyframes pulseGlow {
          0%, 100% {
            box-shadow: inset 0 0 40px rgba(245, 182, 88, 0.05);
          }
          50% {
            box-shadow: inset 0 0 60px rgba(245, 182, 88, 0.25), inset 0 0 80px rgba(245, 182, 88, 0.15);
          }
        }
      `}</style>
    </section>
  );
};

export default TheStorySection;

